<template>
  <div>
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <SearchFormHeader
      :filter-count="filterCount"
      :sort-options="sortOptions"
      :sort-selected-value="sortSelectedValue"
      :total-count="totalCount"
      :is-loading="isShowTotalCountLoading"
      @click-filter="showSearchForm"
      @change-sort="onSelectSort"
    />
    <div class="search-result">
      <div v-if="isLoadingItems" class="loading-section">
        <Loading />
      </div>
      <div v-else class="search-result__loaded">
        <div v-if="isNotEmptyResult">
          <div class="search-result__items">
            <ItemBoxesConnected
              :items="items"
              @click-item-boxes-connected="onClickItem"
            />
          </div>
          <div class="search-result__pagination">
            <Pagination
              class="pagination"
              :current-page="currentPage"
              :total-pages="totalPages"
              @clicked="onClickPageLink"
            />
          </div>
        </div>
        <div v-else-if="isLoadedItems" class="empty-result">
          <div class="body">条件に一致するギフトは見つかりませんでした</div>
        </div>
      </div>
    </div>
    <SearchForm
      :showable-search-form="showableSearchForm"
      :keyword="getCurrentInputtedKeyword()"
      :categories="categories"
      :higher-budget="higherBudget"
      :lower-budget="lowerBudget"
      :scenes="scenes"
      :receiving-kinds="receivingKinds"
      :selected-category-ids="selectedCategoryIds"
      :selected-scene-ids="selectedSceneIds"
      :selected-receiving-kind-ids="selectedReceivingKindIds"
      @hide="hideSearchForm"
      @submit="onSubmitSearchForm"
    />
    <IndexPageRecentViewSection
      @click-item-box="onClickItemBox"
      @click-section-header-link="onClickSectionHeaderLink"
    />
    <div v-if="showEmptyResultContents" class="search-result--empty">
      <IndexPageSceneListSection class="scene" />
      <IndexPageBrandSection />
      <IndexPageCategoriesSection @error="onSectionError" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import qs from 'qs'
import url from 'url'
// NOTE: ネストしたクエリパラメータをパースしてくれる
// ref: https://github.com/ljharb/qs
// ref: https://tech-1natsu.hatenablog.com/entry/2018/07/04/175214
import Pagination from '../../organism/paginations/Pagination.vue'
import SearchFormHeader from '../../organism/search_forms/SearchFormHeader.vue'
import SearchForm from '../../organism/search_forms/SearchForm.vue'
import ItemBoxesConnected from '../../molecules/item_boxes/ItemBoxesConnected.vue'
import Loading from '../../atoms/loadings/Loading.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import SearchItemsQuery from '../../../../../graphqls/public/queries/search_items'
import IndexPageRecentViewSection from '../../organism/homes/sections/IndexPageRecentViewSection.vue'
import IndexPageBrandSection from '../../organism/homes/sections/IndexPageBrandSection.vue'
import IndexPageSceneListSection from '../../organism/homes/sections/IndexPageSceneListSection.vue'
import IndexPageCategoriesSection from '../../organism/homes/sections/IndexPageCategoriesSection.vue'
import SearchedItem from '../../../utils/searched-item.js'
import * as Sentry from '@sentry/browser'

export default {
  components: {
    SearchFormHeader,
    SearchForm,
    Pagination,
    ItemBoxesConnected,
    Loading,
    FeedbackError,
    IndexPageRecentViewSection,
    IndexPageBrandSection,
    IndexPageSceneListSection,
    IndexPageCategoriesSection
  },
  data() {
    return {
      items: [],
      categories: [],
      scenes: [],
      receivingKinds: [],
      sortSelectedValue: 'recommended',
      sortOptions: [
        { value: 'recommended', key: 'おすすめ順' },
        { value: 'new_arrival', key: '新着順' },
        { value: 'price_asc', key: '価格が低い順' },
        { value: 'price_desc', key: '価格が高い順' }
      ],
      isFilterActive: false,
      showableSearchForm: false,
      isLoadingItems: false,
      isLoadingTotalCount: false,
      isLoadedItems: false,
      isAllCategoryChecked: false,
      isAllSceneChecked: false,
      lowerBudget: null,
      higherBudget: null,
      keyword: '',
      selectedCategoryIds: [],
      selectedSceneIds: [],
      selectedReceivingKindIds: [],
      currentPage: 1,
      totalPages: 1,
      totalCount: null,
      totalItemCountOnSearchForm: null,
      showableErrorFeedback: false,
      errorMessage: ''
    }
  },
  computed: {
    showEmptyResultContents() {
      return !this.isNotEmptyResult && this.isLoadedItems
    },
    isNotEmptyResult() {
      return this.items.length !== 0
    },
    isShowTotalCountLoading() {
      return this.isLoadingTotalCount || this.totalCount === null
    },
    hittedItemCount() {
      return this.totalItemCountOnSearchForm !== null
        ? this.totalItemCountOnSearchForm
        : this.items.length
    },
    isLowerBudgetActive() {
      return this.lowerBudget !== null
    },
    isHigherBudgetActive() {
      return this.higherBudget !== null
    },
    isFiltered() {
      return (
        this.isLowerBudgetActive ||
        this.isHigherBudgetActive ||
        this.selectedCategoryIds.length > 0 ||
        this.selectedSceneIds.length > 0 ||
        this.selectedReceivingKindIds.length > 0
      )
    },
    filterCount() {
      return (
        this.selectedCategoryIds.length +
        this.selectedSceneIds.length +
        this.selectedReceivingKindIds.length +
        (this.isLowerBudgetActive ? 1 : 0) +
        (this.isHigherBudgetActive ? 1 : 0)
      )
    }
  },
  async mounted() {
    await this.getItemCategories()
    await this.getScenes()
    await this.getItemReceivingKinds()
    this.initializeParams()
    await this.search(this.currentPage, true)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function () {
      this.initializeParams()
      await this.search(this.currentPage, true)
    }.bind(this)
  },
  methods: {
    onClickItem(itemId) {
      dataLayer.push({
        event: 'click_item_list__item_detail',
        item_id: itemId,
        sort_key: this.sortSelectedValue,
        lower_budget: this.lowerBudget,
        higher_budget: this.higherBudget,
        scene_id:
          this.selectedSceneIds.length > 0
            ? this.selectedSceneIds.join(',')
            : '',
        category_id:
          this.selectedCategoryIds.length > 0
            ? this.selectedCategoryIds.join(',')
            : '',
        item_receiving_kinds:
          this.selectedReceivingKindIds.length > 0
            ? this.selectedReceivingKindIds.join(',')
            : '',
        keyword: this.keyword
      })
    },
    getCurrentInputtedKeyword() {
      return document.querySelector('.search-form__input--text').value
    },
    onSectionError(message) {
      this.errorMessage = message
      this.showableErrorFeedback = true
    },
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.sortSelectedValue = 'recommended'
        this.keyword = ''
        this.lowerBudget = null
        this.higherBudget = null
        this.selectedCategoryIds = []
        this.selectedSceneIds = []
        this.selectedReceivingKindIds = []
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.sortSelectedValue = parsed.sort_key ? parsed.sort_key : 'recommended'
      this.keyword = parsed.keyword ? parsed.keyword : ''
      this.lowerBudget = parsed.lower_budget
        ? Number(parsed.lower_budget)
        : null
      this.higherBudget = parsed.higher_budget
        ? Number(parsed.higher_budget)
        : null
      this.selectedCategoryIds = parsed.category_ids
        ? parsed.category_ids.map(categoryId => Number(categoryId))
        : []
      this.selectedSceneIds = parsed.scene_ids
        ? parsed.scene_ids.map(sceneId => Number(sceneId))
        : []
      this.selectedReceivingKindIds =
        parsed.item_receiving_kinds && parsed.item_receiving_kinds
          ? parsed.item_receiving_kinds.map(itemReceivingKindId =>
              Number(itemReceivingKindId)
            )
          : []
      this.currentPage = parsed.page_number ? Number(parsed.page_number) : 1

      // ========== 特集等で旧パラメータ(category_id/scene_id)を利用している場合に対応するために記載 ==========
      const selectedCategoryId = parsed.category_id
        ? Number(parsed.category_id)
        : null
      if (selectedCategoryId !== null) {
        this.selectedCategoryIds = [selectedCategoryId]
      }

      const selectedSceneId = parsed.scene_id ? Number(parsed.scene_id) : null
      if (selectedSceneId !== null) {
        this.selectedSceneIds = [selectedSceneId]
      }
    },
    async onSelectSort(value) {
      this.sortSelectedValue = value
      this.keyword = this.getCurrentInputtedKeyword()
      this.scrollToTop()
      await this.search(1)
      this.updateUrlFromParams()
    },
    showSearchForm() {
      this.showableSearchForm = true
    },
    hideSearchForm() {
      this.showableSearchForm = false
    },
    async onClickPageLink(page) {
      this.scrollToTop()
      await this.search(page)
      this.updateUrlFromParams()
    },
    async onSubmitSearchForm(
      inputtedLowerBudget,
      inputtedHigherBudget,
      inputtedSelectedCategoryIds,
      inputtedSelectedSceneIds,
      inputtedSelectedReceivingKindIds
    ) {
      this.hideSearchForm()
      this.keyword = this.getCurrentInputtedKeyword()
      this.lowerBudget = inputtedLowerBudget
      this.higherBudget = inputtedHigherBudget
      this.selectedCategoryIds = inputtedSelectedCategoryIds
      this.selectedSceneIds = inputtedSelectedSceneIds
      this.selectedReceivingKindIds = inputtedSelectedReceivingKindIds
      await this.search(1, true)
      this.updateUrlFromParams()
    },
    createParamsObjectFromData(page) {
      const targetPageNumber = page ? page : this.currentPage
      const keyword = this.keyword ? { keyword: this.keyword } : {}
      const sortKey =
        this.sortSelectedValue !== 'recommended'
          ? { sort_key: this.sortSelectedValue }
          : {}
      const lowerBudget = this.lowerBudget
        ? { lower_budget: this.lowerBudget }
        : {}
      const higherBudget = this.higherBudget
        ? { higher_budget: this.higherBudget }
        : {}
      const categoryIds = this.selectedCategoryIds
        ? { category_ids: this.selectedCategoryIds }
        : {}
      const sceneIds = this.selectedSceneIds
        ? { scene_ids: this.selectedSceneIds }
        : {}
      const itemReceivingKinds = this.selectedReceivingKindIds
        ? { item_receiving_kinds: this.selectedReceivingKindIds }
        : {}
      const pageNumber = { page_number: targetPageNumber }
      return Object.assign(
        {},
        sortKey,
        lowerBudget,
        higherBudget,
        categoryIds,
        sceneIds,
        itemReceivingKinds,
        pageNumber,
        keyword
      )
    },
    updateUrlFromParams() {
      const params = this.createParamsObjectFromData()
      if (params.page_number === 1) {
        delete params.page_number
      }
      const paramsString = qs.stringify(params, { arrayFormat: 'brackets' })
      history.pushState(
        paramsString,
        '',
        !paramsString ? '/items' : `/items?${paramsString}`
      )
    },
    async search(pageNumber, isLoadingTotalCount = false) {
      this.items = []
      this.isFilterActive = this.isFiltered
      this.isLoadingItems = true
      this.isLoadedItems = false
      if (isLoadingTotalCount) this.isLoadingTotalCount = true
      try {
        const response = await this.$apollo.provider.clients.publicClient.query(
          {
            query: SearchItemsQuery,
            variables: {
              keyword: this.keyword,
              pageNumber: pageNumber,
              itemPerPage: 30,
              itemReceivingKindIds: this.selectedReceivingKindIds,
              sortKey: this.sortSelectedValue,
              sceneIds: this.selectedSceneIds,
              categoryIds: this.selectedCategoryIds,
              stringLowerBudget:
                this.lowerBudget != null
                  ? this.lowerBudget.toString()
                  : this.lowerBudget,
              stringHigherBudget:
                this.higherBudget != null
                  ? this.higherBudget.toString()
                  : this.higherBudget
            }
          }
        )

        const { currentPage, totalPages, totalCount } =
          response.data.searchItems.pagination
        this.currentPage = currentPage
        this.totalPages = totalPages
        this.totalCount = totalCount
        this.items = response.data.searchItems.items.map(item =>
          SearchedItem.createSearchedItemObject(item)
        )

        // 1ページ以降は検索イベント不要
        if (pageNumber === 1) {
          dataLayer.push({
            event: 'search',
            search_term: this.keyword
          })
        }
      } catch (error) {
        Sentry.captureException(error)
        this.showableErrorFeedback = true
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
      this.isLoadingItems = false
      this.isLoadedItems = true
      if (isLoadingTotalCount) this.isLoadingTotalCount = false
    },
    async getItemCategories() {
      try {
        const res = await axios.get('/api/v1/pickup/item_categories')
        this.categories = res.data.data.map(item_category => {
          return {
            id: item_category.attributes.id,
            name: item_category.attributes.name
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getScenes() {
      try {
        const res = await axios.get('/api/v1/pickup/scenes')
        this.scenes = res.data.data.map(scene => {
          return {
            id: scene.attributes.id,
            name: scene.attributes.name,
            description: scene.attributes.description,
            imageUrl: scene.attributes.image_url
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getItemReceivingKinds() {
      try {
        const res = await axios.get('/api/v1/pickup/item_receiving_kinds')
        this.receivingKinds = res.data.data.map(receivingKind => {
          return {
            id: receivingKind.attributes.id,
            name: receivingKind.attributes.displayName
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    onClickItemBox(itemId) {
      dataLayer.push({
        event: 'click_view_history__item',
        via: 'item_list',
        item_id: itemId
      })
    },
    onClickSectionHeaderLink() {
      dataLayer.push({
        event: 'click_item_list__view_history_list'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.search-result {
  &__items {
    margin-bottom: 16px;
    background-color: $white;

    @media all and (max-width: ($base-contents-width - 1)) {
      margin-bottom: 1px;
    }
  }

  &__pagination {
    margin-bottom: 16px;
    background-color: $gray-3;

    @media all and (max-width: ($base-contents-width - 1)) {
      background-color: $white;
    }

    .pagination {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .item-boxes--connected {
    // NOTE: _base.scssでbodyに対してtext-align:centerが指定されていることへの対応
    text-align: left;
    justify-content: center;
    max-width: $max-contents-width;
    margin: 0 auto;
  }

  .loading-section {
    text-align: center;
    background-color: $white;
    // height 29px で ローディングの下端の高さになる
    height: 29px;
    // padding-top 12px でローディングの上端の高さになるので、 12 + 24 = 52px でローディングの上端から40pxのpaddingになる。
    padding-top: 52px;
    padding-bottom: 40px;
  }

  .empty-result {
    background-color: $white;
    padding-top: 64px;
    padding-bottom: 64px;
  }
}
.search-result--empty {
  .scene {
    max-width: none;
    width: auto;
  }
}
</style>
